import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { FC, useState } from 'react';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import Wizard from '@/components/Wizard/Wizard';
import { StepCount } from '@/components/Wizard/Wizard.types';
import { useCreateMaintenanceFreeze, useCreateNationalProgram } from '@/services/bundles/bundles.service';
import { CreateFreezeProps, FreezeFormErrors, FreezeFormValues } from './CreateFreeze.types';
import { useTranslation } from 'react-i18next';
import { FreezeStepTwo } from '../FormFreezeWizard/FreezeStepTwo';
import { FreezeStepOne } from '../FormFreezeWizard/FreezeStepOne';
import { FreezeStepThree } from '../FormFreezeWizard/FreezeStepThree';
import { DetailedError } from '@/types/error.types';

const EMPTY_FORM_VALUES = {} as FreezeFormValues;
const EMPTY_FORM_ERRORS = {} as FreezeFormErrors;

const CreateFreeze: FC<CreateFreezeProps> = ({
  bundleData,
  isOpen,
  handleClose,
  setFreezeName,
  setConfirmationModalVisible,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<StepCount>(0);
  const [validStepOne, setValidStepOne] = useState(false);
  const [validStepTwo, setValidStepTwo] = useState(false);

  const [formValues, setFormValues] = useState<FreezeFormValues>(EMPTY_FORM_VALUES);

  const [formErrors, setFormErrors] = useState<FreezeFormErrors>(EMPTY_FORM_ERRORS);

  const nationalPrices = [
    formValues.nationalPricePointTwo,
    formValues.nationalPricePointThree,
    formValues.nationalPricePointFour,
  ];

  const filteredNationalPrices = nationalPrices
    .filter((price: string) => price !== '')
    .map((price: string) => Number(price));

  const onClose = () => {
    setStep(0);
    setFormValues(EMPTY_FORM_VALUES);
    setFormErrors(EMPTY_FORM_ERRORS);
    handleClose();
  };

  const handleStepOneNext = () => {
    setFormErrors(EMPTY_FORM_ERRORS);
    setStep(1);
  };

  const { mutate: createMaintenanceFreeze, isLoading: maintenanceFreezeIsLoading } = useCreateMaintenanceFreeze();
  const { mutate, isLoading } = useCreateNationalProgram();

  const handleConfirmMaintenanceFreeze = () => {
    setFreezeName(formValues.freezeName);
    createMaintenanceFreeze(
      {
        bundleId: bundleData?.id,
        freezeName: formValues.freezeName,
        startDate: formValues.freezeStartDate,
        endDate: formValues.freezeEndDate,
      },
      {
        onSuccess: () => {
          onClose();
          setConfirmationModalVisible(true);
          queryClient.invalidateQueries(['freezes']);
        },
        onError: (error) => {
          if ((error as AxiosError).message === 'Request failed with status code 422') {
            showToast({
              id: 'maintenanceFreezeOverlap',
              label: t('error.freezeOverlap'),
              toastType: ToastTypes.Error,
            });
          } else {
            showToast({
              id: 'errorCreatingMaintenanceFreeze',
              label: t('error.freezeStartsSameDay'),
              toastType: ToastTypes.Error,
            });
          }
        },
      },
    );
  };

  const handleConfirmNationalProgram = () => {
    setFreezeName(formValues.freezeName);
    mutate(
      {
        bundleId: bundleData?.id,
        freezeName: formValues.freezeName,
        optInStartDate: formValues.optInStartDate,
        freezeStartDate: formValues.freezeStartDate,
        freezeEndDate: formValues.freezeEndDate,
        defaultPrice: Number(formValues.defaultPrice),
        pricePoints: filteredNationalPrices,
        optOutHidden: !!formValues.optOutHidden,
        redeemableOnline: !!formValues.redeemableOnline,
        visibleOnline: !!formValues.visibleOnline,
        sustainPricingFlag: formValues.sustainPricingFlag === true,
      },
      {
        onSuccess: () => {
          onClose();
          setConfirmationModalVisible(true);
          queryClient.invalidateQueries(['freezes']);
        },
        onError: (error) => {
          if ((error as AxiosError).message === 'Request failed with status code 422') {
            showToast({
              id: 'nationalProgramOverlap',
              label: t('error.freezeOverlap'),
              toastType: ToastTypes.Error,
            });
          } else if (error as DetailedError) {
            // TODO we need to send by BE a error code to avoid i18n there
            const e = error as DetailedError;
            console.warn('Error trying to save freeze: ', e.message, e.details);
            showToast({
              id: 'nationalProgramOverlap',
              label: t('error.freezeOverlap'),
              toastType: ToastTypes.Error,
            });
          } else {
            showToast({
              id: 'errorCreatingNationalProgramFreeze',
              label: t('error.freezeCreatingNationalProgram'),
              toastType: ToastTypes.Error,
            });
          }
        },
      },
    );
  };

  return (
    <Wizard
      currentStep={step}
      showStepIndicator={formValues?.freezeType === 'NATIONAL_PROGRAM'}
      steps={[
        {
          content: (
            <FreezeStepOne
              initialFormData={formValues}
              onChangeFreezeData={(data: FreezeFormValues, isValid) => {
                setFormValues({ ...formValues, ...data });
                setValidStepOne(isValid);
              }}
            />
          ),
          title: t('createFreezeModal.stepOne.title'),
          description: t('createFreezeModal.stepOne.description'),
          onBackClick: onClose,
          onNextClick: () =>
            formValues.freezeType === 'NATIONAL_PROGRAM' ? handleStepOneNext() : handleConfirmMaintenanceFreeze(),
          nextButtonIsLoading: maintenanceFreezeIsLoading,
          nextButtonText: formValues.freezeType === 'MAINTENANCE' ? t('common.confirm') : t('common.next'),
          nextButtonDisabled: !validStepOne,
        },
        {
          content: (
            <FreezeStepTwo
              bundle={bundleData}
              initialFormData={formValues}
              onChangeFreezeData={(data: FreezeFormValues, isValid) => {
                setFormValues({ ...formValues, ...data });
                setValidStepTwo(isValid);
              }}
            />
          ),
          title: t('createFreezeModal.stepTwo.title'),
          description: t('createFreezeModal.stepTwo.description'),
          onBackClick: () => setStep(0),
          onNextClick: () => setStep(2),
          nextButtonDisabled: !validStepTwo,
        },
        {
          content: <FreezeStepThree bundle={bundleData} initialFormData={formValues} />,
          title: t('createFreezeModal.stepThree.title'),
          description: t('createFreezeModal.stepThree.description'),
          onBackClick: () => setStep(1),
          onNextClick: () => handleConfirmNationalProgram(),
          nextButtonIsLoading: isLoading,
          nextButtonText: t('common.confirm'),
        },
      ]}
      isOpen={isOpen}
      handleClose={onClose}
    />
  );
};

export default CreateFreeze;
